import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { useProfile } from "app/context";
import { Trans } from "app/utils";

import { Icon, PlainButton } from "@culturehq/components";
import { makeDelete, makeGet } from "@culturehq/client";

import meta from "images/social/meta_icon.svg";
import linkedin from "images/social/linkedin.svg";
import twitter from "images/social/twitter.svg";
import instagram from "images/social/instagram.svg";
import tiktok from "images/social/tiktok.svg";

export const tiktokAuthenticated = profile => {
  if (!profile.userTiktoks || profile.userTiktoks.length === 0) {
    return false;
  }

  return profile.userTiktoks.find(usertiktok => !!usertiktok.username);
};

const LinkedAccount = ({
  authenticateUser,
  facebookPagesAccounts,
  instagramAccounts,
  linkedinPagesAccounts,
  onUpdateProfile,
  profile,
  type
}) => {
  const tiktokAccounts = profile.userTiktoks.filter(tiktok => !!tiktok?.username);

  const onUnlinkAccount = async () => {
    const { user } = await makeDelete("/profile/social", { socialMedia: type.toLowerCase() });
    onUpdateProfile(user);
  };

  switch (type) {
    case "Facebook":
      return (
        <div className="link-account-container">
          <span className="link-account-name">{profile.userFacebook.name}</span>
          <span className="link-account-related">
            {facebookPagesAccounts.map((facebookPage, index) => (
              <span key={facebookPage.externalId}>
                {facebookPage.name}
                {" "}
                <span className="link-account-related_category"><Trans>(Facebook page)</Trans></span>
                {(facebookPagesAccounts.length - 1) > index && (<><span>,</span>{" "}</>)}
              </span>
            ))}
            {instagramAccounts.map((insatgramPage, index) => (
              <span key={insatgramPage.externalId}>
                {insatgramPage.name}
                {" "}
                <span className="link-account-related_category"><Trans>(Instagram business account)</Trans></span>
                {(instagramAccounts.length - 1) > index && (<><span>,</span>{" "}</>)}
              </span>
            ))}
          </span>
          <PlainButton className="unlink-account-btn" onClick={onUnlinkAccount}>
            <Trans>Unlink your account</Trans>
          </PlainButton>
        </div>
      );
    case "Instagram":
      return (
        <div className="link-account-container">
          <span className="link-account-name">{profile.userInstagram.name}</span>
          <PlainButton className="unlink-account-btn" onClick={onUnlinkAccount}>
            <Trans>Unlink your account</Trans>
          </PlainButton>
        </div>
      );
    case "Linkedin":
      return (
        <div className="link-account-container">
          <span className="link-account-name">
            {profile.userLinkedin.linkedinProfile.localizedFirstName}
            {" "}
            {profile.userLinkedin.linkedinProfile.localizedLastName}
          </span>
          <span className="link-account-related">
            {linkedinPagesAccounts.map((linkedinPage, index) => (
              <span key={linkedinPage.$URN}>
                {linkedinPage.localizedName}
                {" "}
                <span className="link-account-related_category"><Trans>(LinkedIn page)</Trans></span>
                {(linkedinPagesAccounts.length - 1) > index && (<><span>,</span>{" "}</>)}
              </span>
            ))}
          </span>
          <PlainButton className="unlink-account-btn" onClick={onUnlinkAccount}>
            <Trans>Unlink your account</Trans>
          </PlainButton>
        </div>
      );
    case "TikTok":
      return (
        <div className="link-account-container link-account-container__tiktok">
          <span className="link-account-name">
            {tiktokAccounts.map((tiktok, index) => (
              <span key={tiktok.username}>
                {tiktok.username}
                {(tiktokAccounts.length - 1) > index && (<><span>,</span>{" "}</>)}
              </span>
            ))}
          </span>
          <PlainButton className="unlink-account-btn" onClick={onUnlinkAccount}>
            <Trans>Unlink your accounts</Trans>
          </PlainButton>
          <PlainButton className="link-account-btn" onClick={authenticateUser}>
            <Icon icon="plus" />
            <Trans>Link another account</Trans>
          </PlainButton>
        </div>
      );
    case "Twitter":
      return (
        <div className="link-account-container">
          <span className="link-account-name">{profile.userTwitter.name}</span>
        </div>
      );
    default:
      return <></>;
  }
};

const SocialFieldImage = ({ type }) => {
  switch (type) {
    case "Facebook":
      return <img className="social-icon" src={meta} alt="Meta icon" />;
    case "Instagram":
      return <img className="social-icon" src={instagram} alt="Instagram icon" />;
    case "Linkedin":
      return <img className="social-icon" src={linkedin} alt="LinkedIn icon" />;
    case "TikTok":
      return <img className="social-icon" src={tiktok} alt="TikTok icon" />;
    case "Twitter":
      return <img className="social-icon" src={twitter} alt="Twitter icon" />;
    default:
      return <></>;
  }
};

const SocialFieldStatus = ({
  facebookPagesAccounts, instagramAccounts, linkedinPagesAccounts, onUpdateProfile, profile, type
}) => {
  const location = useLocation();

  const isAuthenticated = () => {
    switch (type) {
      case "Facebook":
        return !!profile.userFacebook?.name;
      case "Instagram":
        return !!profile.userInstagram?.name;
      case "Linkedin":
        return !!profile.userLinkedin?.linkedinId;
      case "TikTok":
        return tiktokAuthenticated(profile);
      case "Twitter":
        return !!profile.userTwitter?.name;
      default:
        return false;
    }
  };

  const authFacebookSession = async () => {
    const { url } = await makeGet("/facebook/integration", { url: location.pathname });
    window.location.href = url;
  };

  const authInstagramSession = async () => {
    const { url } = await makeGet("/instagram/integration", { url: location.pathname });
    window.location.href = url;
  };

  const authLinkedinSession = async () => {
    const { url } = await makeGet("/linkedin/integration", { url: location.pathname });
    window.location.href = url;
  };

  const authTiktokSession = async () => {
    const { url } = await makeGet("/tiktok/integration", { url: location.pathname });
    window.location.href = url;
  };

  const authTwitterSession = async () => {
    const { url } = await makeGet("/twitter/integration", { url: location.pathname });
    window.location.href = url;
  };

  const authenticateUser = async () => {
    switch (type) {
      case "Facebook":
        await authFacebookSession();
        break;
      case "Instagram":
        await authInstagramSession();
        break;
      case "Linkedin":
        await authLinkedinSession();
        break;
      case "TikTok":
        await authTiktokSession();
        break;
      case "Twitter":
        await authTwitterSession();
        break;
      default:
        break;
    }
  };

  if (isAuthenticated()) {
    return (
      <LinkedAccount
        authenticateUser={authenticateUser}
        facebookPagesAccounts={facebookPagesAccounts}
        instagramAccounts={instagramAccounts}
        linkedinPagesAccounts={linkedinPagesAccounts}
        onUpdateProfile={onUpdateProfile}
        profile={profile}
        type={type}
      />
    );
  }

  /*
  if (type === "Facebook") {
    return (
      <PlainButton className="link-account-btn link-account-btn-facebook"
      onClick={authenticateUser}>
        <Trans>Login with Facebook</Trans>
      </PlainButton>
    );
  }
  */

  return (
    <PlainButton className="link-account-btn" onClick={authenticateUser}>
      <Icon icon="plus" />
      <Trans>Link your account</Trans>
    </PlainButton>
  );
};

const SocialField = ({
  facebookPagesAccounts,
  instagramAccounts,
  linkedinPagesAccounts,
  onUpdateProfile,
  profile,
  socialMedia
}) => (
  <div className="social-container">
    <SocialFieldImage type={socialMedia.name} />
    <SocialFieldStatus
      facebookPagesAccounts={facebookPagesAccounts}
      instagramAccounts={instagramAccounts}
      linkedinPagesAccounts={linkedinPagesAccounts}
      onUpdateProfile={onUpdateProfile}
      profile={profile}
      type={socialMedia.name}
    />
  </div>
);

const ProfileSocial = () => {
  const { profile, fetchedProfile } = useProfile();
  const [instagramAccounts, setInstagramAccounts] = useState([]);
  const [facebookPagesAccounts, setFacebookPagesAccounts] = useState([]);
  const [linkedinPagesAccounts, setLinkedinPagesAccounts] = useState([]);

  useEffect(
    () => {
      let isMounted = true;

      const getFacebookPages = async () => {
        const { instagram, pages } = await makeGet("/facebook/pages");
        if (!isMounted) return;

        setInstagramAccounts(instagram);
        setFacebookPagesAccounts(pages);
      };

      const getLinkedinPages = async () => {
        const { organizations } = await makeGet("/linkedin/organizations");
        if (!isMounted) return;

        setLinkedinPagesAccounts(organizations);
      };

      if (profile.userFacebook?.name) {
        getFacebookPages();
      }

      if (profile.userLinkedin?.linkedinId) {
        getLinkedinPages();
      }

      return () => {
        isMounted = false;
      };
    },
    [profile]
  );

  const handleUpdateProfile = newProfile => {
    fetchedProfile(newProfile);
  };

  return (
    <div className="Edit-Profile Edit-Social">
      {profile.organization.socialMediaTypes.map(socialMedia => (
        <SocialField
          facebookPagesAccounts={facebookPagesAccounts}
          key={socialMedia.name}
          instagramAccounts={instagramAccounts}
          linkedinPagesAccounts={linkedinPagesAccounts}
          onUpdateProfile={handleUpdateProfile}
          profile={profile}
          socialMedia={socialMedia}
        />
      ))}
      <p>
        <Trans>Please read our</Trans>
        {" "}
        <a href="https://www.culturehq.com/privacy-policy" target="_blank" rel="noopener noreferrer" tabIndex={0}>
          <Trans>Privacy Policy</Trans>
        </a>
      </p>
    </div>
  );
};

export default ProfileSocial;
